import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
//
import Iconify from './Iconify';
import { chatUrl } from 'src/Api';
// ----------------------------------------------------------------------

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.body2,
    height: 48,
    position: 'relative',
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.inactive,
    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      display: 'none',
      position: 'absolute',
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      backgroundColor: theme.palette.primary.main
    }
  })
);

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  mini: PropTypes.bool
};

function NavItem({ item, active, mini }) {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem('user'));
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children, translation } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };
  //comment
  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightBold',
    bgcolor: alpha(theme.palette.primary.main, 0.14),
    '&:before': { display: 'block' }
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium'
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  //      to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle)
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: 'flex',
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (theme) => theme.transitions.create('transform'),
                        ...(isActiveSub && {
                          transform: 'scale(2)',
                          bgcolor: 'primary.main'
                        })
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  const link = (
    <ListItemStyle
      component={RouterLink}
      onClick={() => {
        title == 'Inbox' && window.open(`${chatUrl}${user.id}/0?view_only=0`);
      }}
      to={title != 'Inbox' && path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
        ...(mini && {
          px: 1,
          justifyContent: 'center',
          minHeight: 48
        })
      }}
    >
      <ListItemIconStyle sx={mini ? { mr: 0 } : undefined}>
        <Iconify icon={icon} width={22} height={22} />
      </ListItemIconStyle>
      {!mini && <ListItemText disableTypography primary={title} />}
      {!mini && info && info}
    </ListItemStyle>
  );

  if (mini) {
    return (
      <Tooltip title={title} placement="right" arrow>
        {link}
      </Tooltip>
    );
  }

  return link;
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
  mini: PropTypes.bool
};

export default function NavSection({ navConfig, mini = false, ...other }) {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  return (
    <Box {...other}>
      <List disablePadding>
        {navConfig.map((item) => {
          return <NavItem key={item.title} item={item} active={match} mini={mini} />;
        })}
      </List>
      <Box sx={{ height: mini ? 16 : 50 }} />
    </Box>
  );
}
